<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <!-- Header Section -->
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Currency Rate</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                    <v-text-field
                        v-model="data.quickSearchVal"
                        :label="CommonLabelTextHelper.getLabelText.filterCurrentPage"
                        @keydown="onKeydownQuickSearch"
                    ></v-text-field>
                </v-col>
            </v-row>

            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Advanced Search</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <date-range-select
                            v-model="data.searchFilterDateRangeGrp.dateEffectiveOn"
                            :from-label="formLabelText.dateEffectiveOnFrom"
                            :to-label="formLabelText.dateEffectiveOnTo"
                            :enable-validation="true"
                            @validation-change="onDateRangeValidationChange"
                        />
                        <v-row class="mt-4">
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilter.currencyRateId"
                                    label="Currency Rate ID"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.currencyCodeFrom"
                                    :items="currencyOptions"
                                    item-title="code"
                                    item-value="code"
                                    :label="formLabelText.currencyCodeFrom"
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.currencyCodeTo"
                                    :items="currencyOptions"
                                    item-title="code"
                                    item-value="code"
                                    :label="formLabelText.currencyCodeTo"
                                    clearable
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onSearchClick">
                                <v-icon>mdi-magnify</v-icon>
                                Search
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>

            <!-- Alert Messages -->
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
            <alert-message ref="paginationErrorAlert" :message="errorMessage"></alert-message>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="data.deleteOperation.showConfirmDialog" max-width="500">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                        Confirm Delete
                    </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this currency rate?
                        <br /><br />
                        <strong>Currency Rate ID:</strong>
                        {{ data.deleteOperation.selectedCurrencyRate?.currencyRateId || '' }}
                        <br />
                        <strong>Currency:</strong> {{ data.deleteOperation.selectedCurrencyRate?.currency || '' }}
                        <br /><br />
                        <v-alert type="warning" variant="tonal"> This action cannot be undone. </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="onCancelDelete" :disabled="data.deleteOperation.isLoading"> Cancel </v-btn>
                        <v-btn
                            color="error"
                            @click="onConfirmDelete"
                            :loading="data.deleteOperation.isLoading"
                            :disabled="data.deleteOperation.isLoading"
                        >
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Action Buttons and Table -->
            <v-card>
                <v-card-title>
                    <div class="d-flex flex-wrap align-center ga-2 w-100">
                        <v-btn color="success" @click="onImportClick"> Create by import </v-btn>
                        <v-btn variant="text" @click="onSearchClick">
                            <v-icon>mdi-cached</v-icon>
                            Refresh
                        </v-btn>
                        <v-spacer></v-spacer>
                        <export-to-csv
                            :data="currentPageData"
                            :csv-header="data.csvExportConfig.csvHeader"
                            :filename="data.csvExportConfig.filename"
                            :formatter="csvFormatter"
                        ></export-to-csv>
                    </div>
                </v-card-title>

                <v-card-text>
                    <!-- Generic Table Component -->
                    <GenericListTable
                        :pagination="pagination"
                        :current-page-data="currentPageData"
                        :headers="headers"
                        :primary-key="'currencyRateId'"
                        :action-header-value="actionHeaderItem.value"
                        :quick-search-value="data.quickSearchVal"
                        :loading-message="'Loading more currency rates...'"
                        :is-loading="data.deleteOperation.isLoading"
                        @page-size-change="onPageSizeChange"
                        @page-select="onPageSelect"
                        @next-page="onNextPage"
                        @previous-page="onPreviousPage"
                    >
                        <template #table-actions="{ item, isLoading }">
                            <v-btn
                                :disabled="isLoading"
                                :loading="isLoading"
                                variant="text"
                                icon="mdi-delete"
                                color="error"
                                @click="onDeleteCurrencyRateClick(item)"
                            >
                            </v-btn>
                        </template>
                    </GenericListTable>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import * as _ from 'lodash';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { CurrencyRateLabelTextHelper } from '../../utils/labelTextHelper/CurrencyRateLabelTextHelper';
import { CommonLabelTextHelper } from '../../utils/labelTextHelper/CommonLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import GenericListTable from '../../components/GenericListTable.vue';
import { CurrencyRateHeaderHelper } from '../../utils/headerHelper/CurrencyRateHeaderHelper';
import { CsvFormatHelper } from '../../utils/CsvFormatHelper';
import { usePagination } from '../../composables/usePagination';
import { useMasterData } from '../../composables/useMasterData';
import { useUrlFilters } from '../../composables/useUrlFilters';
import { CurrencyRateFilterDefinitions } from '../../utils/filterDefinitions/CurrencyRateFilterDefinitions';

export default defineComponent({
    name: 'ListCurrencyRate',

    components: {
        ExportToCsv,
        AlertMessage,
        GenericListTable,
        DateRangeSelect
    },

    setup() {
        const route = useRoute();

        // Load master data options for dropdowns
        const { currencyOptions, loadAllMasterData } = useMasterData();

        const data = reactive({
            panel: [0],
            quickSearchVal: '',
            errorAlertMessage: '',
            searchFilterDateRangeGrpDefault: {},
            searchFilterDateRangeGrp: {
                dateEffectiveOn: {
                    from: null,
                    to: null
                }
            },
            searchFilterDefault: {},
            searchFilter: {
                currencyRateId: null,
                currencyCodeFrom: null,
                currencyCodeTo: null
            },
            csvExportConfig: {
                filename: 'exportCurrencyRate.csv',
                csvHeader: [...CurrencyRateHeaderHelper.getCsvHeader()]
            },
            deleteOperation: {
                isLoading: false,
                showConfirmDialog: false,
                selectedCurrencyRate: null
            },
            validation: {
                hasErrors: false
            }
        });

        // Initialize URL filter sync
        const { syncFiltersToUrl, restoreFiltersFromUrl, watchRouteChanges } = useUrlFilters({
            filterFields: CurrencyRateFilterDefinitions,
            onFiltersRestored: (filters) => {
                // Apply restored filters to reactive state
                Object.keys(filters).forEach((key) => {
                    if (key.startsWith('date') && (key.endsWith('From') || key.endsWith('To'))) {
                        const dateField = key.replace(/From|To$/, '');
                        const direction = key.endsWith('From') ? 'from' : 'to';
                        data.searchFilterDateRangeGrp[dateField][direction] = filters[key];
                    } else {
                        data.searchFilter[key] = filters[key];
                    }
                });
            },
            validationContext: {
                currencyOptions
            }
        });

        // Setup pagination
        const paginationConfig = {
            apiEndpoint: '/api/staff/currencyRate/list',
            buildApiParams: (page: number) => {
                const params: any = {
                    effectiveDateFrom: Formatter.convertDateFromInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateEffectiveOn.from
                    ),
                    effectiveDateTo: Formatter.convertDateToInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateEffectiveOn.to
                    )
                };

                // Add currency rate ID filter
                if (data.searchFilter.currencyRateId?.trim()) {
                    params.currencyRateId = data.searchFilter.currencyRateId.trim();
                }

                // Add currency filters
                if (data.searchFilter.currencyCodeFrom) {
                    params.currencyCodeFrom = data.searchFilter.currencyCodeFrom;
                }
                if (data.searchFilter.currencyCodeTo) {
                    params.currencyCodeTo = data.searchFilter.currencyCodeTo;
                }

                return params;
            },
            initialPageSize: 20
        };

        const {
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            removeItemFromAllPages,
            initialize
        } = usePagination(paginationConfig);

        onMounted(async () => {
            // Load master data FIRST (for dropdown validation)
            await loadAllMasterData();

            // Clone defaults BEFORE restoring from URL
            data.searchFilterDateRangeGrpDefault = _.cloneDeep(data.searchFilterDateRangeGrp);
            data.searchFilterDefault = _.cloneDeep(data.searchFilter);

            // Restore filters from URL (if any)
            const hasUrlFilters = Object.keys(route.query).length > 0;
            if (hasUrlFilters) {
                restoreFiltersFromUrl();
            }

            // Initialize pagination (single API call with restored filters)
            await initialize();

            // Watch for future route changes (back/forward buttons)
            watchRouteChanges();
        });

        return {
            formLabelText: CurrencyRateLabelTextHelper.getLabelText,
            CommonLabelTextHelper,
            actionHeaderItem: CurrencyRateHeaderHelper.getActionHeaderItem(),
            headers: [...CurrencyRateHeaderHelper.getHeaders()],
            data,
            // Master data options for dropdowns
            currencyOptions,
            // Pagination
            pagination,
            currentPageData,
            errorMessage,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            resetPagination,
            loadPage,
            removeItemFromAllPages,
            initialize,
            // URL Filters
            syncFiltersToUrl
        };
    },

    methods: {
        onKeydownQuickSearch(evt: any) {
            if (evt.key === 'Enter') {
                // Quick search is now handled by GenericListTable component
            }
        },
        async onResetClick() {
            this.data.searchFilterDateRangeGrp = _.cloneDeep(this.data.searchFilterDateRangeGrpDefault);
            this.data.searchFilter = _.cloneDeep(this.data.searchFilterDefault);

            // Clear URL query params
            await this.syncFiltersToUrl({});

            // Reload with defaults
            this.resetPagination();
            await this.loadPage(1, true);
        },
        async onSearchClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before searching';
                this.$refs.errorAlert?.showAlert();
                return;
            }

            // Flatten filters for URL (handle nested date ranges)
            const currentFilters = {
                ...this.data.searchFilter,
                dateEffectiveOnFrom: this.data.searchFilterDateRangeGrp.dateEffectiveOn.from,
                dateEffectiveOnTo: this.data.searchFilterDateRangeGrp.dateEffectiveOn.to
            };

            // Sync to URL BEFORE API call
            await this.syncFiltersToUrl(currentFilters);

            // Reset pagination and load first page with new filters
            this.resetPagination();
            await this.loadPage(1, true);
        },
        onDateRangeValidationChange(validationStatus: { hasErrors: boolean }) {
            this.data.validation.hasErrors = validationStatus.hasErrors;
        },
        async onImportClick() {
            await this.$router.push({ path: PathDefinition.importCurrencyRate });
        },
        csvFormatter: function (value: unknown, key: string, row: Record<string, unknown>): unknown {
            return CsvFormatHelper.formatDateTimeCsvValue(value as string, key);
        },
        onPageSizeChange() {
            this.changePageSize();
        },
        onPageSelect(page: number) {
            this.selectPage(page);
        },
        onNextPage() {
            this.nextPage();
        },
        onPreviousPage() {
            this.previousPage();
        },

        // Utility method for handling errors consistently
        handleError(error: any, message: string, showAlert: boolean = true) {
            console.error(message, error);
            if (showAlert) {
                this.data.errorAlertMessage = typeof error === 'string' ? error : message;
                this.$refs.errorAlert.showAlert();
            }
        },

        // Utility method for handling API responses
        handleApiResponse(response: any, successCallback: () => void, errorMessage?: string) {
            if (response.success) {
                successCallback();
            } else {
                const message = errorMessage || response.errors || 'Operation failed';
                this.handleError(response.errors, message);
            }
        },

        onDeleteCurrencyRateClick(item: any) {
            this.data.deleteOperation.selectedCurrencyRate = item;
            this.data.deleteOperation.showConfirmDialog = true;
        },

        onCancelDelete() {
            this.data.deleteOperation.showConfirmDialog = false;
            this.data.deleteOperation.selectedCurrencyRate = null;
        },

        async onConfirmDelete() {
            if (!this.data.deleteOperation.selectedCurrencyRate) {
                return;
            }

            this.data.deleteOperation.isLoading = true;

            try {
                const currencyRateId = this.data.deleteOperation.selectedCurrencyRate.currencyRateId;
                const requestUrl = `/api/staff/currencyRate/batchDelete`;
                const requestData = {
                    currencyRateIdList: [currencyRateId]
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                this.handleApiResponse(
                    response,
                    () => {
                        // Remove the item using the pagination composable
                        this.removeItemFromAllPages(currencyRateId, 'currencyRateId');

                        // Close dialog and reset state
                        this.data.deleteOperation.showConfirmDialog = false;
                        this.data.deleteOperation.selectedCurrencyRate = null;
                    },
                    'Failed to delete currency rate'
                );
            } catch (error) {
                this.handleError(error, 'An unexpected error occurred while deleting the currency rate');
            } finally {
                this.data.deleteOperation.isLoading = false;
            }
        }
    }
});
</script>

<style>
.validation-error {
    color: red;
}
</style>
